<script setup lang="ts">
import { Checkbox } from '@/components/ui/checkbox';
import { TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-vue-next';

const props = defineProps<{
  taskIds: string[];
  selectedTaskIds: string[];
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  showSelection: boolean;
  showClientColumn: boolean;
  showProjectColumn: boolean;
  readOnly: boolean;
  sticky?: boolean;
}>();

const emit = defineEmits<{
  (event: 'toggle-all', taskIds: string[]): void;
  (event: 'sort', field: string): void;
}>();

const allSelected = computed(() => (
  props.showSelection
  && props.taskIds.length > 0
  && props.taskIds.every(taskId => props.selectedTaskIds.includes(taskId))
));

function sortIcon(field: string) {
  if (props.sortField !== field) return ArrowUpDown;
  return props.sortDirection === 'asc' ? ArrowUp : ArrowDown;
}

const stickyHeadClass = computed(() => props.sticky ? 'task-table-sticky-head' : '');
</script>

<template>
  <TableHeader :class="['task-table-header', sticky ? 'task-table-header--sticky' : '']">
    <TableRow>
      <TableHead v-if="showSelection" :class="['w-[2.75rem] min-w-[2.75rem] max-w-[2.75rem]', stickyHeadClass]">
        <button
          type="button"
          :disabled="readOnly"
          :class="readOnly ? 'inline-flex opacity-60' : 'inline-flex cursor-pointer'"
          aria-label="Select all tasks"
          @click.stop="emit('toggle-all', taskIds)"
        >
          <Checkbox :checked="allSelected" :disabled="readOnly" />
        </button>
      </TableHead>

      <TableHead
        v-for="column in [
          { field: 'title', label: 'Task', visible: true },
          { field: 'status', label: 'Status', visible: true },
          { field: 'priority', label: 'Priority', visible: true },
          { field: 'assignee', label: 'Assignee', visible: true },
          { field: 'clientId', label: 'Client', visible: showClientColumn },
          { field: 'projectId', label: 'Project', visible: showProjectColumn },
          { field: 'dueDate', label: 'Due Date', visible: true },
        ].filter(column => column.visible)"
        :key="column.field"
        :class="['cursor-pointer hover:bg-muted/50', stickyHeadClass]"
        @click="emit('sort', column.field)"
      >
        <div class="flex items-center gap-1">
          <span>{{ column.label }}</span>
          <component :is="sortIcon(column.field)" class="size-4" />
        </div>
      </TableHead>
    </TableRow>
  </TableHeader>
</template>

<style scoped>
.task-table-header {
  background: color-mix(in srgb, var(--muted) 58%, var(--card));
}

.task-table-header--sticky {
  position: sticky;
  top: 0;
  z-index: 20;
}

.task-table-sticky-head {
  background: color-mix(in srgb, var(--muted) 58%, var(--card));
  backdrop-filter: blur(14px);
}
</style>
